import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/hero';
import { getFilmById } from '@/utils/content';

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((next: number) => {
    setActive((next + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % heroSlides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused]);

  const slide = heroSlides[active];
  const film = getFilmById(slide.filmId);

  return (
    <section className="page-shell pt-6 md:pt-8">
      <div
        className="relative h-[460px] w-full overflow-hidden rounded-lg border border-background-300/60 bg-background-200 md:h-[560px]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {heroSlides.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
              index === active ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
          >
            <img
              src={item.image}
              alt={`${item.headline} 焦点内容`}
              title={`${item.headline} · 成人影视推荐`}
              className={`h-full w-full object-cover object-top transition-transform duration-[6000ms] ease-out ${
                index === active ? 'scale-100' : 'scale-105'
              }`}
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/90 via-transparent to-background-50/40" />

        <div className="relative flex h-full flex-col justify-end p-6 pb-20 md:max-w-[62%] md:justify-center md:p-12 md:pb-14">
          <div key={slide.id} className="animate-rise">
            <p className="eyebrow mb-3">{slide.kicker}</p>
            <h1 className="text-3xl font-semibold leading-tight tracking-wide text-foreground-950 md:text-[44px]">
              {slide.headline}
            </h1>
            <p className="mt-4 max-w-xl text-[13px] leading-7 text-foreground-700 md:text-sm">
              {slide.description}
            </p>
            {film && (
              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-foreground-500">
                <span className="flex items-center gap-1 text-primary-400">
                  <i className="ri-star-fill text-[10px]" />
                  {film.rating.toFixed(1)}
                </span>
                <span>{film.year}</span>
                <span>{film.region}</span>
                <span>{film.subcategory}</span>
                <span>{film.duration} 分钟</span>
              </div>
            )}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slide.filmId}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-file-text-line" />
                查看作品资料
              </Link>
              {film && (
                <Link
                  to={`/category/${film.category}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-400/70 bg-background-50/40 px-5 py-2.5 text-[13px] text-foreground-800 backdrop-blur-sm transition-colors hover:border-primary-500/60 hover:text-primary-400"
                >
                  浏览同类内容
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 left-6 flex items-center gap-2 md:bottom-7 md:left-12">
          {heroSlides.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => go(index)}
              aria-label={`切换到第 ${index + 1} 张焦点内容`}
              className={`h-1 cursor-pointer rounded-full transition-all duration-300 ${
                index === active ? 'w-8 bg-primary-500' : 'w-4 bg-foreground-700/50 hover:bg-foreground-600'
              }`}
            />
          ))}
        </div>

        <div className="absolute bottom-5 right-5 flex items-center gap-2 md:bottom-7 md:right-8">
          <button
            type="button"
            onClick={() => go(active - 1)}
            aria-label="上一张"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-400/60 bg-background-50/60 text-foreground-800 backdrop-blur-sm transition-colors hover:border-primary-500/60 hover:text-primary-400"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            type="button"
            onClick={() => go(active + 1)}
            aria-label="下一张"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-400/60 bg-background-50/60 text-foreground-800 backdrop-blur-sm transition-colors hover:border-primary-500/60 hover:text-primary-400"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>
    </section>
  );
}